import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { QrCode, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';

export default function VisitorQRScannerPage() {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const scanningRef = useRef(false);
  const jsQRRef = useRef<any>(null);
  const initializingRef = useRef(false); // Track if already initializing
  const [scanning, setScanning] = useState(false);
  const [cameraPermission, setCameraPermission] = useState<'pending' | 'granted' | 'denied'>('pending');
  const [scannedData, setScannedData] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);
  const isQrScannerEnabled = import.meta.env.VITE_ENABLE_QR_SCANNER !== 'false';

  // Load jsQR library
  useEffect(() => {
    if (!isQrScannerEnabled) {
      console.log('🔒 QR scanner disabled, skipping camera initialization');
      return;
    }

    const loadJsQR = async () => {
      return new Promise<void>((resolve) => {
        if ((window as any).jsQR) {
          console.log('✅ jsQR already loaded');
          jsQRRef.current = (window as any).jsQR;
          resolve();
          return;
        }

        console.log('📦 Loading jsQR from CDN...');
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.js';
        script.onload = () => {
          console.log('✅ jsQR loaded successfully');
          jsQRRef.current = (window as any).jsQR;
          resolve();
        };
        script.onerror = () => {
          console.error('❌ Failed to load jsQR from CDN');
          resolve();
        };
        document.body.appendChild(script);
      });
    };

    loadJsQR();
  }, [isQrScannerEnabled]);

  const handleQRCodeScanned = useCallback(async (qrData: string) => {
    if (scannedData === qrData) return; // Avoid duplicates
    
    setScannedData(qrData);
    scanningRef.current = false;
    setScanning(false);

    try {
      setSuccessMessage(`QR Scanned: ${qrData}`);

      setTimeout(() => {
        if (qrData.startsWith('http')) {
          window.location.href = qrData;
        } else {
          navigate(`/poi/${qrData}`);
        }
      }, 2000);
    } catch (err) {
      console.error('QR processing error:', err);
      setError('Errore nel processamento QR. Riprova.');
      setScanning(true);
    }
  }, [scannedData, navigate]);

  const startScanning = useCallback(() => {
    console.log('🔍 startScanning called');
    console.log('videoRef.current:', videoRef.current);
    console.log('jsQRRef.current:', jsQRRef.current);
    
    if (!videoRef.current || !jsQRRef.current) {
      console.error('❌ videoRef or jsQR not ready');
      return;
    }
    
    scanningRef.current = true;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    const cropCanvas = document.createElement('canvas');
    const cropContext = cropCanvas.getContext('2d');
    if (!context || !cropContext) {
      console.error('❌ Canvas context failed');
      return;
    }

    let scanCount = 0;
    const scanFrame = (imageData: ImageData) => {
      if (!jsQRRef.current) return null;
      return jsQRRef.current(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: 'attemptBoth',
      });
    };

    const scan = () => {
      if (!videoRef.current || !context || !cropContext || !scanningRef.current) return;

      try {
        const videoWidth = videoRef.current.videoWidth;
        const videoHeight = videoRef.current.videoHeight;

        if (videoWidth === 0 || videoHeight === 0) {
          if (scanCount % 30 === 0) {
            console.warn(`⚠️ Video not ready yet (${scanCount} frames): ${videoWidth}x${videoHeight}`);
          }
          scanCount++;
          requestAnimationFrame(scan);
          return;
        }

        canvas.width = videoWidth;
        canvas.height = videoHeight;
        context.imageSmoothingEnabled = false;

        if (scanCount === 0) {
          console.log(`✅ Video ready for scanning: ${videoWidth}x${videoHeight}`);
        }
        scanCount++;

        context.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
        let imageData = context.getImageData(0, 0, canvas.width, canvas.height);
        let code = scanFrame(imageData);

        if (!code || !code.data) {
          const cropSize = Math.min(canvas.width, canvas.height) * 0.6;
          const cropX = Math.max(0, (canvas.width - cropSize) / 2);
          const cropY = Math.max(0, (canvas.height - cropSize) / 2);

          cropCanvas.width = cropSize;
          cropCanvas.height = cropSize;
          cropContext.drawImage(canvas, cropX, cropY, cropSize, cropSize, 0, 0, cropSize, cropSize);
          const croppedData = cropContext.getImageData(0, 0, cropSize, cropSize);
          code = scanFrame(croppedData);

          if (code && code.data) {
            console.log('✅ QR code detected in center crop:', code.data);
            handleQRCodeScanned(code.data);
            return;
          }
        } else {
          console.log('✅ QR code detected:', code.data);
          handleQRCodeScanned(code.data);
          return;
        }
      } catch (err) {
        if (scanCount % 30 === 0) {
          console.error('⚠️ Scan error:', err);
        }
      }

      if (scanningRef.current) {
        requestAnimationFrame(scan);
      }
    };

    scan();
  }, [handleQRCodeScanned]);

  // Initialize camera - RUN ONLY ONCE
  useEffect(() => {
    if (!isQrScannerEnabled) {
      console.log('🔒 QR scanner disabled, camera initialization skipped');
      setCameraPermission('denied');
      setInitialized(true);
      return;
    }

    // Prevent multiple initializations (React Strict Mode runs effects twice in dev)
    if (initializingRef.current) {
      console.log('⏸️ Camera init already in progress, skipping...');
      return;
    }
    
    initializingRef.current = true;
    console.log('🎥 Starting camera initialization...');

    const initCamera = async () => {
      try {
        if (!navigator?.mediaDevices?.getUserMedia) {
          console.error('❌ getUserMedia not supported');
          setCameraPermission('denied');
          setError('Browser non supportato. Usa Chrome, Firefox, o Safari.');
          return;
        }

        setCameraPermission('pending');
        let stream: MediaStream | null = null;

        try {
          console.log('📷 Requesting camera with facingMode=environment...');
          stream = await navigator.mediaDevices.getUserMedia({
            video: { 
              facingMode: 'environment',
              width: { ideal: 1920 },
              height: { ideal: 1080 },
              frameRate: { ideal: 30 },
            },
          });
        } catch (err) {
          console.warn('⚠️ Environment camera failed, trying lower-res or any camera...');
          try {
            stream = await navigator.mediaDevices.getUserMedia({
              video: { 
                facingMode: 'environment',
                width: { ideal: 1280 },
                height: { ideal: 720 },
                frameRate: { ideal: 30 },
              },
            });
          } catch (innerErr) {
            console.warn('⚠️ Fallback to any available camera...');
            stream = await navigator.mediaDevices.getUserMedia({
              video: { 
                width: { ideal: 1280 },
                height: { ideal: 720 },
                frameRate: { ideal: 30 },
              },
            });
          }
        }

        console.log('✅ Stream obtained, assigning to video element...');
        
        // Retry with progressive delays to ensure video element is ready
        let retryCount = 0;
        const maxRetries = 10; // 100ms total
        const assignStream = () => {
          if (videoRef.current && stream) {
            console.log('✅ Video element found, attaching stream...');
            videoRef.current.srcObject = stream;
            
            // Use loadedmetadata - fires when metadata is available
            const onLoadedMetadata = async () => {
              console.log('✅ loadedmetadata event fired!');
              videoRef.current?.removeEventListener('loadedmetadata', onLoadedMetadata);
              console.log(`📺 Video dimensions: ${videoRef.current?.videoWidth}x${videoRef.current?.videoHeight}`);
              
              // Force play with explicit error handling
              try {
                console.log('▶️ Calling play()...');
                await videoRef.current?.play();
                console.log('✅ play() succeeded');
              } catch (playErr) {
                console.error('❌ play() failed:', playErr);
              }

              // Set states
              console.log('🟢 Setting camera as granted, starting scan...');
              setCameraPermission('granted');
              setScanning(true);
              setInitialized(true);
              
              // Start scanning after 500ms to ensure video is stable
              setTimeout(() => {
                console.log('🔍 Starting QR scan loop...');
                startScanning();
              }, 500);
            };

            videoRef.current.addEventListener('loadedmetadata', onLoadedMetadata);
            console.log('👂 loadedmetadata listener attached');
            
            // Timeout safety net
            const timeoutId = setTimeout(() => {
              if (!initialized) {
                console.warn('⏱️ Camera init timeout - forcing start');
                setCameraPermission('granted');
                setScanning(true);
                setInitialized(true);
                startScanning();
              }
            }, 5000);

            return () => clearTimeout(timeoutId);
          } else if (retryCount < maxRetries) {
            retryCount++;
            console.log(`⏳ Video element not ready, retry ${retryCount}/${maxRetries}...`);
            setTimeout(assignStream, 50);
          } else {
            console.error('❌ videoRef or stream is null after retries');
            if (!videoRef.current) console.error('  - videoRef.current is null (DOM not ready)');
            if (!stream) console.error('  - stream is null');
          }
        };
        
        assignStream();
      } catch (err: any) {
        console.error('❌ Camera error:', err);
        setCameraPermission('denied');
        
        if (err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError') {
          console.error('Permission denied by user or browser');
          setError('Accesso fotocamera negato. Consenti l\'accesso nelle impostazioni del browser.');
        } else if (err?.name === 'NotFoundError' || err?.name === 'DevicesNotFoundError') {
          console.error('No camera device found');
          setError('Fotocamera non trovata. Verifica il collegamento.');
        } else if (err?.name === 'NotSupportedError') {
          console.error('Not supported - HTTPS/localhost required');
          setError('Fotocamera non supportata. Usa HTTPS o localhost.');
        } else {
          console.error('Unknown error:', err?.name, err?.message);
          setError('Errore fotocamera. Riprova con un browser diverso.');
        }
      }
    };

    initCamera();

    return () => {
      scanningRef.current = false;
      if (videoRef.current?.srcObject) {
        const tracks = (videoRef.current.srcObject as MediaStream).getTracks();
        tracks.forEach((track) => track.stop());
      }
    };
  }, []);

  const handleRetry = () => {
    setScannedData(null);
    setError(null);
    setSuccessMessage(null);
    setInitialized(false);
    setCameraPermission('pending');
    setScanning(true);
  };

  const handleRequestPermission = () => {
    setError(null);
    setCameraPermission('pending');
    setInitialized(false);
    window.location.reload();
  };

  if (!isQrScannerEnabled) {
    return (
      <div className="min-h-screen bg-mp-navy/95 flex items-center justify-center p-4">
        <div className="w-full max-w-md overflow-hidden rounded-[32px] border border-mp-gray-700 bg-white shadow-2xl">
          <div className="bg-gradient-to-r from-mp-navy to-mp-sky px-6 py-8 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-3xl bg-white/10 text-white shadow-lg shadow-mp-sky/30">
              <QrCode size={28} />
            </div>
            <p className="text-xs font-semibold uppercase tracking-[0.35em] text-mp-gray-100/80">Esperienza immersiva</p>
            <p className="mt-4 text-xl font-semibold text-mp-white">Ora puoi scansionare i QR con la tua fotocamera</p>
          </div>

          <div className="space-y-6 px-6 py-8 text-mp-gray-800">
            <p className="text-sm leading-7 text-mp-gray-800">
              Vivi l&apos;esperienza immersiva senza lasciare l&apos;app: posiziona il codice QR al centro dell&apos;inquadro e lascia che la fotocamera faccia il resto.
            </p>

            <div className="rounded-3xl border border-mp-gray-700 bg-mp-white-800/95 p-4 shadow-inner">
              <p className="text-sm font-semibold text-mp-gray">Consigli rapidi</p>
              <ul className="mt-3 space-y-3 text-sm text-mp-gray-800">
                <li>• Mantieni il codice ben visibile e non tagliato.</li>
                <li>• Usa una buona illuminazione e sfrutta il flash del telefono se serve.</li>
                <li>• Allontanati leggermente se il QR è troppo vicino all&apos;obiettivo.</li>
              </ul>
            </div>

            <div className="rounded-3xl border border-mp-gray-700 bg-white p-4">
              <p className="text-sm font-semibold text-gray-800">Semplice, veloce, immersivo</p>
              <p className="mt-2 text-sm text-mp-gray-800">
                La pagina QR è disabilitata, ma puoi continuare a usare il tuo telefono per leggere i codici QR e accedere ai contenuti del museo.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-4">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="p-4 bg-white/10 rounded-full">
                <QrCode size={40} className="text-white" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Scansiona QR Code</h1>
            <p className="text-blue-100 text-sm">Punta la fotocamera verso il codice QR dell'attrazione</p>
          </div>

          {/* Camera or Status */}
          <div className="px-6 py-8">
            {cameraPermission !== 'denied' && (
              <>
                {/* Video Feed with Loading Overlay */}
                <div className="relative bg-black rounded-lg overflow-hidden mb-6" style={{ aspectRatio: '1' }}>
                  <video
                    ref={videoRef}
                    autoPlay
                    muted
                    playsInline
                    className="w-full h-full object-cover"
                  />

                  {/* Loading overlay when initializing */}
                  {cameraPermission === 'pending' && (
                    <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center">
                      <Loader2 size={40} className="text-blue-400 animate-spin mb-3" />
                      <p className="text-sm text-blue-100">Inizializzazione fotocamera...</p>
                    </div>
                  )}

                  {/* Scanning border when granted */}
                  {cameraPermission === 'granted' && scanning && (
                    <div className="absolute inset-0 border-4 border-blue-400 animate-pulse">
                      {/* Scanning indicator */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-32 h-32 border-4 border-blue-400 rounded-lg"></div>
                      </div>
                    </div>
                  )}
                </div>

                {cameraPermission === 'granted' && (
                  <>
                    {successMessage && (
                      <div className="mb-6 flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                        <CheckCircle size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-green-900">{successMessage}</p>
                          <p className="text-xs text-green-700 mt-1">Reindirizzamento in corso...</p>
                        </div>
                      </div>
                    )}

                    {error && (
                      <div className="mb-6 flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-red-900">{error}</p>
                          <button
                            onClick={handleRetry}
                            className="text-xs text-red-600 hover:text-red-700 font-medium mt-2 underline"
                          >
                            Riprova
                          </button>
                        </div>
                      </div>
                    )}

                    {!successMessage && !error && (
                      <div className="text-center text-sm text-slate-600 mb-4">
                        <Loader2 size={16} className="animate-spin inline-block mr-2" />
                        In attesa del QR code...
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {cameraPermission === 'denied' && (
              <div className="flex flex-col items-center justify-center py-12 gap-4 text-center">
                <AlertCircle size={32} className="text-red-600" />
                <div>
                  <p className="text-sm font-medium text-slate-900 mb-1">Accesso fotocamera negato</p>
                  <p className="text-xs text-slate-600 mb-4">
                    Per scansionare i QR code, consenti l'accesso alla fotocamera nelle impostazioni del browser.
                  </p>
                  <div className="space-y-2 text-xs text-slate-600 text-left bg-slate-50 p-3 rounded mb-4">
                    <p><strong>Come risolvere:</strong></p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Clicca il pulsante "Riprova" per concedere i permessi</li>
                      <li>Se il browser chiede, seleziona "Consenti" per la fotocamera</li>
                      <li>Assicurati di usare HTTPS o localhost</li>
                      <li>Se non funziona, prova un browser diverso (Chrome, Firefox)</li>
                    </ul>
                  </div>
                </div>
                <button
                  onClick={handleRequestPermission}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Riprova
                </button>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-slate-50 border-t flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="text-sm text-slate-600 hover:text-slate-900 font-medium"
            >
              ← Indietro
            </button>
            <div className="text-xs text-slate-500">
              {scanning ? '🔴 Attiva' : '⚫ In pausa'}
            </div>
          </div>
        </div>

        {/* Info */}
        <div className="bg-white/10 backdrop-blur rounded-lg p-4 text-center">
          <p className="text-sm text-blue-100">
            💡 I codici QR sono disponibili presso ogni attrazione del museo
          </p>
        </div>
      </div>
    </div>
  );
}
